// src/shared/changelog/useAutoOpenWhatsNew.ts
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useIsGuidedTourRunning } from "@shared/tour/GuidedTour";
import { getChangelog } from "./getChangelog";
import { groupEntriesByMonth, type ChangelogMonthGroup } from "./grouping";
import { readLastAutoOpen } from "./autoOpenStore";
import { shouldAutoOpenRelease } from "./autoOpenRules";
import { localStorageSeenStore } from "./seenStore";
import { useSeenEntries } from "./useSeenEntries";

/**
 * Where the modal may open itself (§2.1).
 *
 * The exclusion list in the decision is four items long and three of them are
 * already impossible here. This hook runs inside the SideMenu footer, which
 * only exists under `AppLayout` — so `/login` and the Auth0 callback, both
 * public routes rendered by `PublicRoutes`, are out by construction rather than
 * by a check that could rot when a path is renamed. A running tour and the
 * once-per-release cap are conditions, not routes, and live below.
 *
 * That leaves `/changelog`: the archive already *is* the announcement, and a
 * dialog over it would be announcing a page to someone reading that page.
 */
function isAutoOpenableView(pathname: string): boolean {
  return pathname !== "/changelog";
}

/**
 * Opens the What's new modal by itself, once, on the first product view of the
 * session (CTX-500 §2.1, §2.5, §2.8).
 *
 * ── It asks; it does not spend the turn ─────────────────────────────────────
 *
 * This hook decides and calls back. Recording the auto-open is the caller's,
 * and the caller must do it only once the dialog is **actually on screen**
 * (`WhatsNewBoundary` passes `onShown` to the modal for exactly that).
 *
 * That split is not tidiness, it is the bug it was written for. Spending the
 * release's one turn at the moment of *deciding* means anything that unmounts
 * the caller in between — and plenty does, since the rail swaps its whole tree
 * when the profile lands and `PrivateRoute` swaps its own when Auth0 settles —
 * leaves the turn spent and the announcement unseen. The reader gets a dialog
 * for a second, or none at all, and never sees that release again. Recording on
 * arrival instead makes a remount harmless: nothing was spent, so the next
 * mount simply decides again.
 *
 * ── The first session is quiet ──────────────────────────────────────────────
 *
 * Seeding happens here rather than in a second hook because it has to land
 * *before* the decision reads the seen set. A new device is seeded with the
 * whole visible feed (§2.8) and then finds nothing unread, so the silence falls
 * out of `shouldAutoOpenRelease` rather than needing a clause of its own.
 *
 * @param onAutoOpen Called at most once per mount, when every condition holds,
 * with the id of the release to announce. The caller records it once shown.
 * @param isEnabled Whether this tenant has the changelog at all. `false` skips
 * the fetch entirely — a request whose result can never render is traffic for
 * nothing, which is the rule `useChangelogNewItems` already follows.
 */
export function useAutoOpenWhatsNew(
  onAutoOpen: (releaseId: string) => void,
  isEnabled = true
): void {
  const axiosClient = useAxiosAuth0();
  const { pathname } = useLocation();
  const isTourRunning = useIsGuidedTourRunning();
  const { seenEntries, isLoaded: isSeenLoaded } = useSeenEntries();

  const [release, setRelease] = useState<ChangelogMonthGroup | null>(null);
  const [isFeedReady, setIsFeedReady] = useState(false);
  const hasAutoOpened = useRef(false);

  // Held in a ref so a caller that re-creates the callback on every render does
  // not re-run the decision below; the decision's inputs are the rules' inputs
  // and nothing else.
  const onAutoOpenRef = useRef(onAutoOpen);
  useEffect(() => {
    onAutoOpenRef.current = onAutoOpen;
  }, [onAutoOpen]);

  /**
   * The feed, and the seeding that has to precede any decision about it.
   *
   * `getChangelog()` rather than `getLatestReleaseGroup()` because §2.8 seeds
   * the **whole visible feed**, not the newest month — and both come out of the
   * same memoised request, so asking for the wider one costs nothing.
   *
   * A failure leaves `isFeedReady` false and the modal simply never opens
   * itself. The card swallows its own fetch failure for the same reason: an
   * interruption nobody asked for is not the place to report a network error.
   */
  useEffect(() => {
    if (!isEnabled) return undefined;
    let alive = true;

    const prepare = async () => {
      const [entries, hasBootstrapped] = await Promise.all([
        getChangelog(axiosClient),
        localStorageSeenStore.hasBootstrapped(),
      ]);
      if (!alive) return;

      if (!hasBootstrapped) {
        await localStorageSeenStore.bootstrap(
          entries.map((entry) => entry.entryKey)
        );
        if (!alive) return;
      }

      setRelease(groupEntriesByMonth(entries)[0] ?? null);
      setIsFeedReady(true);
    };

    prepare().catch(() => {
      /* no auto-open */
    });

    return () => {
      alive = false;
    };
  }, [axiosClient, isEnabled]);

  /**
   * A tour on screen **defers** the auto-open rather than cancelling it: the
   * reader is mid-walkthrough of something else, and interrupting that is the
   * exact failure §2.1 is about — but a release they have not read is still
   * unread when the tour ends. The same holds for `/changelog`; arriving from
   * it onto a product view is still the first product view worth interrupting.
   */
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (!isFeedReady || !isSeenLoaded || !release) return;
    if (isTourRunning) return;
    if (!isAutoOpenableView(pathname)) return;
    if (
      !shouldAutoOpenRelease({
        release,
        seenEntries,
        lastAutoOpen: readLastAutoOpen(),
        now: Date.now(),
      })
    ) {
      return;
    }

    // Asked for, not yet spent. The caller records it when the dialog reaches
    // the screen; a click on the card never records at all, because a manual
    // open must not cost the release its turn (§2.1).
    hasAutoOpened.current = true;
    onAutoOpenRef.current(release.id);
  }, [
    isFeedReady,
    isSeenLoaded,
    isTourRunning,
    pathname,
    release,
    seenEntries,
  ]);
}
